import {createSlice} from '@reduxjs/toolkit';

/**
 * Stable key for task-selection. Based on the set of triptask uids
 * in the edited row, so it matches the same row across analytics refetches (the uids don't change
 * when a task is reassigned, only their task association does). Keys the `pendingTaskSelections`
 * reconciliation state below.
 *
 * @param {Array<string|number>} triptaskUidList - The row's triptask uids.
 * @return {string} A stable, order-independent key (empty string when there are no uids).
 */
export function buildTaskSelectionKey(triptaskUidList) {
  if (!Array.isArray(triptaskUidList) || triptaskUidList.length === 0) {
    return '';
  }
  return triptaskUidList
    .map((uid) => {
      return String(uid);
    })
    .sort()
    .join(',');
}

/**
 * Shipped defaults for the Table Configuration panel, exported so its "Reset" uses the very same
 * values the slice starts from rather than a second copy that can drift out of step.
 */
export const DEFAULT_SPLIT_BY = ['equipment', 'task', 'implement', 'operator', 'rec'];

export const DEFAULT_SORT = {
  zones: {label: 'acreage', desc: true},
  equipment: {label: 'acreage', desc: true},
  nonAcArea: {label: 'time', desc: true},
};
/**
 * One order list across all three tables. The key namespaces are disjoint (`time`/`acreage`/... on
 * Zones, `coverage`/`eqRate`/... on Equipment), so each table picks out its own and ignores the
 * rest - and a table that drives its columns from this list shows nothing for a key it is missing,
 * which is why every orderable column belongs here.
 */
export const DEFAULT_FIELD_ORDER = [
  // One order for every column, splits included.
  //
  // Split By used to be a second list with its own order (`DEFAULT_SPLIT_BY`), which meant the
  // panel showed two lists you could only reorder within - a measure could never sit between two
  // split columns even though the table draws them side by side. The split keys lead because
  // that is where the table has always drawn them; drag one and it moves for real now.
  'equipment',
  'task',
  'implement',
  'operator',
  'rec',
  // Zones
  'time',
  'acreage',
  'rate',
  'entryExit',
  'passCount',
  'speed',
  // Other Areas
  'nonAcTotal',
  'nonAcDriving',
  'nonAcIdling',
  'nonAcStopped',
  'nonAcTowing',
  'nonAcEntryExit',
  // Equipment
  'coverage',
  'eqRate',
  'inField',
  'eqOutField',
  'eqIdle',
  'onTime',
  'timeTowing',
];
export const DEFAULT_FIELD_VISIBILITY = {
  // --- Zones ---
  time: true,
  acreage: true,
  rate: true,
  // Off by default, like `entryExit`: Passes answers a niche question and costs width in a table
  // that already scrolls sideways. A *default*, not a reset - once the user ticks it the session
  // carries that choice back, and switching views must not undo it.
  passCount: false,
  speed: true,
  // One toggle for the whole "Entry -> Exit" interval. Off by default: niche, and the column costs
  // width in a table that is already wide.
  entryExit: false,

  // --- Other Areas ---
  // Its own keys. The three tables' display fields are independent: hiding a column on one must
  // not hide the same-named column on another, and its panel shows no control for the others'
  // columns, so a shared key would be invisible cross-talk.
  //
  // One key per duration: detail draws a column each, compact stacks them all on the machine's
  // line and ignores these entirely. Stopped and Towing default off - Driving and Idling answer
  // the common question, and the table doubles in width with all four on.
  nonAcTotal: true,
  nonAcDriving: true,
  nonAcIdling: true,
  nonAcStopped: false,
  nonAcTowing: false,
  nonAcEntryExit: false,
  // Other Areas task split. Off by default - one row per machine, which is how production shows
  // it and what 204 of 208 customers had before `nonAcDisplayTask` was removed.
  nonAcShowTask: false,

  // --- Equipment ---
  machineType: true,
  coverage: true,
  eqRate: true,
  inField: true,
  onTime: true,
  eqOutField: true,
  eqIdle: true,
  timeTowing: true,

  // --- Split By (Zones only) ---
  showTask: true,
  showImplement: false,
  showOperator: false,
  // The rec/work order is a dimension rows split on, like task - it is listed under Split By.
  showRec: true,
  // Equipment is a split dimension like task/implement/operator rather than an assumption.
  // Off, a block collapses to one row covering every machine that worked it.
  splitEquipment: true,
};

const cropviewSlice = createSlice({
  name: 'cropview',
  initialState: {
    // Loading states
    loading: true,
    nonAcLoading: true,
    pathsLoading: true,
    nonAcPathsLoading: true,
    gpsLoaded: false,

    // Date range
    dates: {start: null, end: null, max: null},

    // Data dictionaries
    zonesDicts: {},
    nonAcZonesDict: {},
    geofencesDict: {},
    nonAcGeofencesDict: {},
    vehicleSNDict: {},
    implementSNDict: {},
    taskConfigIdDict: {},
    operatorDict: {},

    // Analytics data
    zoneAnalytics: [],
    nonAcZoneAnalytics: [],
    nonAcZoneAnalyticsByShift: {},
    equipmentAnalytics: [],
    blockEquipmentAnalytics: {},
    pendingTaskSelections: {},

    // Path data — legacy LineLayer segment shape, nested [vehicleSN][intelliblockNum][taskId].
    nonAcPathData: [],
    pathQueryStatus: {
      success: true,
      pathDataTooLarge: false,
    },
    vehicleLatestLocationDict: {},
    vehicleSerialNumbersInDisplayedAnalytics: [],
    pathsVehicleDays: 0,

    // UI state
    displayedView: 'zones', // zones, equipment, nonAcArea
    // Sort is tracked per view since each view exposes different sort fields.
    // Shape: {[view]: {label: <sortField>, desc: <boolean>}}
    sort: {...DEFAULT_SORT},
    pivotLevels: ['block', 'equipment'],
    nonAcPivotLevels: ['area', 'equipment'],
    zoneGroupBy: 'block', // block, field, or region
    nonAcGroupBy: 'area',
    // 'compact' renders each row as a wrapping LABEL-value strip; 'detail' is the column table.
    //
    // Compact is the default because the list shares its width with the map: a column table has
    // to scroll horizontally in that pane, while the strips reflow. Persisted per session
    // (see `/cropview/updateCropviewSessionData`), so switching to detail sticks.
    listViewMode: 'compact',
    // Seeded from the exported defaults so a Reset and a fresh session can never
    // disagree - they had drifted to twelve differing keys.
    visibleFields: {...DEFAULT_FIELD_VISIBILITY},

    /**
     * Split dimensions in the order their columns appear.
     *
     * Order only - whether each is on lives with the rest of the column switches in
     * `visibleFields` (`showTask`, `showImplement`, `showOperator`, `splitEquipment`). Keeping
     * enablement in one place and ordering in another beats two lists that can disagree about
     * which columns exist.
     */
    splitBy: [...DEFAULT_SPLIT_BY],

    /**
     * Measure columns in display order. Membership is the order; whether each is drawn still
     * comes from `visibleFields`, so a hidden column keeps its place when switched back on.
     */
    fieldOrder: [...DEFAULT_FIELD_ORDER],

    // Day/shift split of the table.
    //
    // `daySplitEnabled` is the user's toggle; the split only actually renders when the range
    // spans more than one shift and the server sent a breakdown (it withholds one above the
    // bucket cap, since long ranges are already heavy enough aggregated).
    // Off by default: the aggregate is what CropView has always shown, and a user who wants the
    // day breakdown asks for it.
    daySplitEnabled: false,
    zoneAnalyticsByShift: null,
    equipmentAnalyticsByShift: null,
    shiftBuckets: null,
    shiftSplitCapped: false,
    maxShiftSplitBuckets: 10,

    // Map state
    showMap: true,
    // Shared with Cabview (persisted at the session root): cluster vehicle icons on the map.
    displayClusters: true,
    zoneZoom: '',
    // When true, the map background-fetches idle heatmap points for the selected date range.
    fetchIdleHeatMap: false,
    // Idle heatmap points keyed by `${databaseName}|${start}|${end}` to avoid re-fetching the same range.
    idleHeatMapCache: {},
    // True while the idle heatmap points are being background-fetched (drives the legend "loading…" hint).
    idleHeatMapLoading: false,
    vehicleZoom: '',
    pointZoom: null,
    selectedVehicleSN: '',
    displayZones: true,
    showOutOfZoneTrips: false,
    zoneLabelsIndex: 1,
    zoneColorIndex: 0,
    pathColorMode: 'vehicle', // 'task', 'vehicle', 'speed'
    useCustomSpeedTarget: false,
    customSpeedTargetMinKph: 4.5, // Default values as placeholders
    customSpeedTargetMaxKph: 5.5,
    showGpsTime: false,

    // Misc
    lastTripTaskEndTime: '',
    processingInProgress: false,
    mapPopupResetTick: 0,
    reiActiveDict: {},
    recList: [],
    refreshRequired: false,
    taskAcreageCapping: false,
    taskAcreageCapTracker: {},
    taskAcreageCapTrackerTask: '',
    showLoggingGaps: false,

    // Devices
    devicesList: [],
    activeDevices: {
      samsara: {},
      geotab: {},
      johndeere: {},
      pellenc: {},
    },
  },
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setNonAcLoading(state, action) {
      state.nonAcLoading = action.payload;
    },
    setPathsLoading(state, action) {
      state.pathsLoading = action.payload;
    },
    setNonAcPathsLoading(state, action) {
      state.nonAcPathsLoading = action.payload;
    },
    setGpsLoaded(state, action) {
      state.gpsLoaded = action.payload;
    },
    setDates(state, action) {
      state.dates = action.payload;
    },
    setCoverageData(state, action) {
      state.vehicleSNDict = action.payload.vehicleSNDict;
      state.implementSNDict = action.payload.implementSNDict;
      state.zonesDicts = action.payload.zonesDicts;
      state.geofencesDict = action.payload.geofencesDict;
      state.taskConfigIdDict = action.payload.taskConfigIdDict;
      state.zoneAnalytics = action.payload.zoneAnalytics;
      state.equipmentAnalytics = action.payload.equipmentAnalytics;
      state.recList = action.payload.recList;
    },
    // Phase 1 of the streamed getCropviewData response: dicts needed for filters + map boundaries.
    setCoverageMetadata(state, action) {
      state.vehicleSNDict = action.payload.vehicleSNDict;
      state.implementSNDict = action.payload.implementSNDict;
      state.zonesDicts = action.payload.zonesDicts;
      state.geofencesDict = action.payload.geofencesDict;
      state.taskConfigIdDict = action.payload.taskConfigIdDict;
      state.operatorDict = action.payload.operatorDict;
      state.recList = action.payload.recList;
    },
    // Phase 2 of the streamed getCropviewData response: worker analytics for the table.
    setCoverageAnalytics(state, action) {
      state.zoneAnalytics = action.payload.zoneAnalytics;
      state.equipmentAnalytics = action.payload.equipmentAnalytics;
      // Per-shift breakdown arrives alongside the aggregate, so toggling between them is free.
      state.zoneAnalyticsByShift = action.payload.zoneAnalyticsByShift ?? null;
      state.equipmentAnalyticsByShift = action.payload.equipmentAnalyticsByShift ?? null;
      state.shiftBuckets = action.payload.shiftBuckets ?? null;
      state.shiftSplitCapped = Boolean(action.payload.shiftSplitCapped);
      state.maxShiftSplitBuckets = action.payload.maxShiftSplitBuckets ?? 10;
    },
    setDaySplitEnabled(state, action) {
      state.daySplitEnabled = action.payload;
    },
    setNonAcCoverageData(state, action) {
      state.nonAcZoneAnalytics = action.payload.zoneAnalytics;
      state.nonAcZoneAnalyticsByShift = action.payload.zoneAnalyticsByShift;
      state.nonAcShiftBuckets = action.payload.shiftBuckets ?? null;
      state.nonAcZonesDict = action.payload.nonAcZonesDict;
      state.nonAcGeofencesDict = action.payload.nonAcGeofencesDict;
    },
    clearData(state) {
      state.zoneAnalytics = [];
      state.equipmentAnalytics = [];
      state.pendingTaskSelections = {};
    },
    setPendingTaskSelection(state, action) {
      const {key, taskId} = action.payload;
      if (!key) return;
      state.pendingTaskSelections[key] = taskId;
    },
    clearPendingTaskSelection(state, action) {
      delete state.pendingTaskSelections[action.payload];
    },
    clearNonAcTableData(state) {
      state.nonAcZoneAnalytics = [];
      state.nonAcZoneAnalyticsByShift = {};
    },
    setDisplayedView(state, action) {
      state.displayedView = action.payload;
    },
    setViewSort(state, action) {
      const {view, label, desc} = action.payload;
      if (typeof state.sort !== 'object' || state.sort === null) {
        state.sort = {};
      }
      state.sort[view] = {label, desc};
    },
    setPivotLevels(state, action) {
      state.pivotLevels = action.payload;
    },
    setNonAcPivotLevels(state, action) {
      state.nonAcPivotLevels = action.payload;
    },
    setVisibleFields(state, action) {
      state.visibleFields = {...state.visibleFields, ...action.payload};
    },
    setListViewMode(state, action) {
      state.listViewMode = action.payload;
    },
    setSplitBy(state, action) {
      state.splitBy = action.payload;
    },
    setFieldOrder(state, action) {
      state.fieldOrder = action.payload;
    },
    setZoneGroupBy(state, action) {
      state.zoneGroupBy = action.payload;
    },
    setNonAcGroupBy(state, action) {
      state.nonAcGroupBy = action.payload;
    },
    setZoneZoom(state, action) {
      state.zoneZoom = action.payload;
    },
    setVehicleZoom(state, action) {
      state.vehicleZoom = action.payload;
    },
    setSelectedVehicleSN(state, action) {
      state.selectedVehicleSN = action.payload;
    },
    setPointZoom(state, action) {
      state.pointZoom = action.payload;
    },
    updateFetchIdleHeatMap(state, action) {
      state.fetchIdleHeatMap = action.payload;
    },
    setIdleHeatMapCacheEntry(state, action) {
      const {cacheKey, pointsByVehicle} = action.payload;

      // Limit the number of idle heatmap cache entries to 3
      // to prevent the cache from growing too large.
      const maxIdleHeatMapCacheEntries = 3;

      // Refresh LRU order when the same range is stored again.
      if (Object.prototype.hasOwnProperty.call(state.idleHeatMapCache, cacheKey)) {
        delete state.idleHeatMapCache[cacheKey];
      }

      // Add the new entry to the cache and remove the oldest entry if the cache is full.
      state.idleHeatMapCache[cacheKey] = pointsByVehicle;
      const cacheKeys = Object.keys(state.idleHeatMapCache);
      while (cacheKeys.length > maxIdleHeatMapCacheEntries) {
        delete state.idleHeatMapCache[cacheKeys[0]];
        cacheKeys.shift();
      }
    },
    setIdleHeatMapLoading(state, action) {
      state.idleHeatMapLoading = action.payload;
    },
    setShowLoggingGaps(state, action) {
      state.showLoggingGaps = action.payload;
    },
    setLastTripTaskEndTime(state, action) {
      state.lastTripTaskEndTime = action.payload;
    },
    setProcessingInProgress(state, action) {
      state.processingInProgress = action.payload;
    },
    resetMapPopups(state) {
      state.mapPopupResetTick += 1;
    },
    setReiActive(state, action) {
      const reiList = action.payload;
      const reiActiveDict = {};
      reiList.forEach((rei) => {
        reiActiveDict[rei.zoneId] = rei;
      });
      state.reiActiveDict = reiActiveDict;
    },
    setRefreshRequired(state, action) {
      state.refreshRequired = action.payload;
    },
    setTaskAcreageCapping(state, action) {
      state.taskAcreageCapping = action.payload;
    },
    setTaskAcreageCapTracker(state, action) {
      state.taskAcreageCapTracker = action.payload;
    },
    setTaskAcreageCapTrackerTask(state, action) {
      state.taskAcreageCapTrackerTask = action.payload;
    },
    setPathQueryStatus(state, action) {
      state.pathQueryStatus = action.payload;
    },
    setNonAcPathData(state, action) {
      state.nonAcPathData = action.payload;
    },
    setVehicleLatestLocationDict(state, action) {
      state.vehicleLatestLocationDict = action.payload;
    },
    setVehicleSerialNumbersInDisplayedAnalytics(state, action) {
      state.vehicleSerialNumbersInDisplayedAnalytics = action.payload;
    },
    setPathsVehicleDays(state, action) {
      state.pathsVehicleDays = action.payload;
    },
    setBlockEquipmentAnalytics(state, action) {
      state.blockEquipmentAnalytics = action.payload;
    },
    setDisplayZonesOptions(state, action) {
      const {stateName, value} = action.payload;
      if (value !== undefined && value !== null) {
        state[stateName] = value;
      }
    },
    setDevices(state, action) {
      if (action.payload != null) {
        const devicesActive = JSON.parse(JSON.stringify(action.payload));
        const devicesList = [];
        Object.keys(action.payload).forEach((source) => {
          Object.keys(action.payload[source]).forEach((key) => {
            devicesList.push(action.payload[source][key]);
            if (!action.payload[source][key].active) {
              delete devicesActive[source][key];
            }
          });
        });
        state.devicesList = devicesList;
        state.activeDevices = devicesActive;
      }
    },
  },
});

export const {
  setLoading,
  setNonAcLoading,
  setPathsLoading,
  setNonAcPathsLoading,
  setGpsLoaded,
  setDates,
  setCoverageData,
  setCoverageMetadata,
  setCoverageAnalytics,
  setDaySplitEnabled,
  setNonAcCoverageData,
  clearData,
  setPendingTaskSelection,
  clearPendingTaskSelection,
  clearNonAcTableData,
  setDisplayedView,
  setViewSort,
  setPivotLevels,
  setNonAcPivotLevels,
  setVisibleFields,
  setZoneGroupBy,
  setNonAcGroupBy,
  setZoneZoom,
  setVehicleZoom,
  setSelectedVehicleSN,
  setPointZoom,
  updateFetchIdleHeatMap,
  setIdleHeatMapCacheEntry,
  setIdleHeatMapLoading,
  setShowLoggingGaps,
  setLastTripTaskEndTime,
  setProcessingInProgress,
  resetMapPopups,
  setReiActive,
  setRefreshRequired,
  setTaskAcreageCapping,
  setTaskAcreageCapTracker,
  setTaskAcreageCapTrackerTask,
  setPathQueryStatus,
  setNonAcPathData,
  setVehicleLatestLocationDict,
  setVehicleSerialNumbersInDisplayedAnalytics,
  setPathsVehicleDays,
  setBlockEquipmentAnalytics,
  setDisplayZonesOptions,
  setDevices,
  setListViewMode,
  setSplitBy,
  setFieldOrder,
} = cropviewSlice.actions;

export default cropviewSlice.reducer;
